import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AiRewrite = () => {
  const [text, setText] = useState('');
  const [language, setLanguage] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [rewrittenText, setRewrittenText] = useState('');
  const [history, setHistory] = useState(() => {
    const storedHistory = localStorage.getItem('history');
    return storedHistory ? JSON.parse(storedHistory) : [];
  });
  const [errors, setErrors] = useState({});
  const [isPrompt, setIsPrompt] = useState(false);
  const outputTextareaRef = useRef(null);

  useEffect(() => {
    const savedIsPrompt = localStorage.getItem('is_prompt');
    if (savedIsPrompt) {
      setIsPrompt(savedIsPrompt === 'true');
    }
  }, []);

  const adjustTextareaHeight = (textarea) => {
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight * 1.1}px`; // Adjust the height to fit the content with extra space
  };

  const validate = () => {
    const newErrors = {};
    if (!text) newErrors.text = 'Tekst is verplicht';
    if (!language) newErrors.language = 'Taal is verplicht';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generatePrompt = () => {
    if (!validate()) return;

    let prompt = `Rewrite the following text in ${language}`;
    if (targetAudience) {
      prompt += ` for a ${targetAudience} audience: `;
    } else {
      prompt += ': ';
    }
    prompt += text;

    setRewrittenText(prompt);
  };

  const handleRewriteClick = async () => {
    if (!validate()) return;

    let prompt = `Rewrite the following text in ${language}`;
    if (targetAudience) {
      prompt += ` for a ${targetAudience} audience: `;
    } else {
      prompt += ': ';
    }
    prompt += text;

    const apiKey = localStorage.getItem('openai_api_key');
    if (!apiKey) {
      alert('API key is not set. Please set it in the General Settings.');
      return;
    }

    try {
      const response = await axios.post('https://api.openai.com/v1/chat/completions', {
        model: 'gpt-3.5-turbo-0125',
        messages: [{ role: 'system', content: prompt }],
        max_tokens: 1024,
        temperature: 0.7,
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        }
      });

      const rewrittenText = response.data.choices[0].message.content.trim();
      setRewrittenText(rewrittenText);
    } catch (error) {
      console.error('Error rewriting text:', error);
    }
  };

  const saveRewrittenTextToHistory = () => {
    if (rewrittenText) {
      const timestamp = new Date().toLocaleString();
      const newEntry = { originalText: text, rewrittenText, timestamp };
      const updatedHistory = [...history, newEntry];
      setHistory(updatedHistory);
      localStorage.setItem('history', JSON.stringify(updatedHistory));
    }
  };

  useEffect(() => {
    saveRewrittenTextToHistory();
  }, [rewrittenText]);

  const copyToClipboard = () => {
    if (outputTextareaRef.current) {
      outputTextareaRef.current.select();
      document.execCommand('copy');
      // Optionally, you can provide user feedback that the text has been copied
      // e.g., using a toast message or changing the button text temporarily
    }
  };

  return (
    <div>
      <div className="banner-settings-wrapper">
        <h1>AI instellingen</h1>
        {!isPrompt ? (
          <div>
            <div className="banner-settings-switch">
              <div className="banner-settings-inner-content">
                <div className="banner-settings-inner-content-details_input">
                  <h5>Herschrijf tekst via ChatGPT</h5>
                  <p>Voer de tekst in die je wil laten herschrijven op basis van jouw variabelen.</p>
                  <div className="banner-settings-inner-content-toggle">
                    <textarea
                      className="banner-settings-inner-content-details_input-text"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      rows="5"
                    />
                  </div>
                  {errors.text && <p style={{ color: 'red' }}>{errors.text}</p>}
                  <br></br>
                  <div className="banner-settings-inner-content-details_input">
                    <h5>Taal</h5>
                    <input
                      type="text"
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      placeholder="Bijv. Engels, Nederlands"
                    />
                  </div>
                  {errors.language && <p style={{ color: 'red' }}>{errors.language}</p>}
                  <br></br>
                  <div className="banner-settings-inner-content-details_input">
                    <h5>Doelgroep (optioneel)</h5>
                    <input
                      type="text"
                      value={targetAudience}
                      onChange={(e) => setTargetAudience(e.target.value)}
                      placeholder="Bijv. kinderen, studenten"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="save-button-div" style={{ marginBottom: '20px' }}>
              <button className="save-button copy-button" onClick={generatePrompt}>
                Genereer Prompt
              </button>
            </div>
            <div className="banner-settings-switch">
              <div className="banner-settings-inner-content-toggle">
                <textarea
                  className="banner-settings-inner-content-details_input-text"
                  ref={outputTextareaRef}
                  value={rewrittenText}
                  readOnly
                  rows="5"
                />
              </div>
            </div>
            <div className="save-button-div">
              <button className="copy-button" onClick={copyToClipboard} style={{ marginLeft: '10px' }}>
                Kopieer
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="banner-settings-switch">
              <div className="banner-settings-inner-content">
                <div className="banner-settings-inner-content-details_input">
                  <h5>Herschrijf tekst automatisch</h5>
                  <p>Voer de tekst in die je automatisch wil herschrijven op basis van jouw variabelen.</p>
                  <div className="banner-settings-inner-content-toggle">
                    <textarea
                      className="banner-settings-inner-content-details_input-text"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      rows="5"
                    />
                  </div>
                  {errors.text && <p style={{ color: 'red' }}>{errors.text}</p>}
                  <br></br>
                  <div className="banner-settings-inner-content-details_input">
                    <h5>Taal</h5>
                    <input
                      type="text"
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      placeholder="Bijv. Engels, Nederlands"
                    />
                  </div>
                  {errors.language && <p style={{ color: 'red' }}>{errors.language}</p>}
                  <br></br>
                  <div className="banner-settings-inner-content-details_input">
                    <h5>Doelgroep (optioneel)</h5>
                    <input
                      type="text"
                      value={targetAudience}
                      onChange={(e) => setTargetAudience(e.target.value)}
                      placeholder="Bijv. kinderen, studenten"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="save-button-div" style={{ marginBottom: '20px' }}>
              <button className="save-button" onClick={handleRewriteClick}>
                Herschrijf tekst
              </button>
            </div>
            <div className="banner-settings-switch">
              <div className="banner-settings-inner-content-toggle">
                <textarea
                  className="banner-settings-inner-content-details_input-text"
                  ref={outputTextareaRef}
                  value={rewrittenText}
                  readOnly
                  rows="5"
                />
              </div>
            </div>
            <div className="save-button-div">
              <button className="copy-button" onClick={copyToClipboard} style={{ marginLeft: '10px' }}>
                Kopieer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AiRewrite;
